import { Box, FormControl, FormHelperText, FormLabel, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  QualificationSchemeDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { endOfDay, startOfUtcDay } from "./schemeValidityDates";

interface Props {
  eventId: string;
  scheme: QualificationSchemeDto | null;
  isEditable: boolean;
}

// Announced once and binding from then on, which is why the fields lock at publication.
export const QualificationSchemeFields = ({ eventId, scheme, isEditable }: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  // The period saves as a pair, so the first date the buyer picks waits here for the second.
  const [draft, setDraft] = useState<Partial<QualificationSchemeDto>>({});
  const validFrom = scheme?.validFrom ?? draft.validFrom ?? null;
  const validTo = scheme?.validTo ?? draft.validTo ?? null;

  const setValidity = (next: Partial<QualificationSchemeDto>) => {
    const merged: Partial<QualificationSchemeDto> = {
      validFrom: validFrom ?? undefined,
      validTo: validTo ?? undefined,
      ...next,
    };
    setDraft(merged);
    if (!merged.validFrom || !merged.validTo) return;
    updateField({
      publicContext: { qualificationScheme: { validFrom: merged.validFrom, validTo: merged.validTo } },
    });
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mb="8">
      <Heading as="h3" size="sm" mb="1">
        {t("The qualification system")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="5">
        {t(
          "Announced once. Suppliers can apply for the whole period, and competitions under the system run among those admitted.",
        )}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
        <FormControl>
          <FormLabel fontSize="sm">{t("The system runs from")}</FormLabel>
          <ThemedDatePicker
            date={validFrom}
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validFrom: date ? startOfUtcDay(date) : undefined })}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">{t("until")}</FormLabel>
          <ThemedDatePicker
            date={validTo}
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validTo: date ? endOfDay(date) : undefined })}
          />
          <FormHelperText>
            {t("Suppliers can apply right up to this date.")}
          </FormHelperText>
        </FormControl>
      </SimpleGrid>
    </Box>
  );
};

